import { filter, flatMap, forEach, get, includes, isEmpty, map, maxBy, some, values } from 'lodash';

import { modelTypeCellSegmentation } from 'components/Pages/Jobs/inferenceFieldsOptions';
import {
  getOrchestrationKey,
  InferenceModelData,
  ModelInference,
  OrchestrationInference,
  ResultNameOrchestrations,
  SlideInferenceResults,
} from 'interfaces/calculateFeatures';
import { OrchestrationBySlideByResultName, OrchestrationBySlideByType } from 'utils/orchestrationSelections';

export interface LatestCellSegmentationSelectionUpdate {
  slideIds: string[];
  model: InferenceModelData;
  modelType: string;
  orchestration: OrchestrationInference;
}

export interface LatestSpotDetectionSelectionUpdate {
  slideIds: string[];
  resultKey: string;
  orchestration: OrchestrationInference;
}

const getOrchestrationCreatedAtValue = (orchestration?: OrchestrationInference) => {
  const createdAtValue = orchestration?.createdAt ? new Date(orchestration.createdAt).valueOf() : 0;
  return Number.isFinite(createdAtValue) ? createdAtValue : 0;
};

export const hasSelectedResultInputs = (
  selections?: OrchestrationBySlideByType | OrchestrationBySlideByResultName | null
) => some(values(selections), (selectedByKey) => !isEmpty(selectedByKey));

export const hasSelectedPostProcessingInputs = ({
  selectedPostprocessedOrchestrations,
  selectedPipsOrchestrations,
}: {
  selectedPostprocessedOrchestrations?: OrchestrationBySlideByResultName | null;
  selectedPipsOrchestrations?: OrchestrationBySlideByResultName | null;
}) =>
  hasSelectedResultInputs(selectedPostprocessedOrchestrations) || hasSelectedResultInputs(selectedPipsOrchestrations);

export const buildLatestCellSegmentationSelectionUpdates = ({
  slides,
  inferenceModels,
  selectedOrchestrations,
}: {
  slides: SlideInferenceResults[];
  inferenceModels?: ModelInference[];
  selectedOrchestrations?: OrchestrationBySlideByType;
}): LatestCellSegmentationSelectionUpdate[] => {
  const candidates = flatMap(
    filter(inferenceModels, (model) => model.modelType === modelTypeCellSegmentation.apiModelValue),
    (model) =>
      map(model.orchestrations, (orchestration) => ({
        model,
        orchestration,
      }))
  );
  const updatesByOrchestrationId: Record<string, LatestCellSegmentationSelectionUpdate> = {};

  forEach(slides, (slide) => {
    if (get(selectedOrchestrations, [slide.slideId, modelTypeCellSegmentation.apiModelValue])) {
      return;
    }

    const latestCandidate = maxBy(
      filter(candidates, ({ orchestration }) => includes(orchestration.slideIds, slide.slideId)),
      ({ orchestration }) => getOrchestrationCreatedAtValue(orchestration)
    );

    if (!latestCandidate) {
      return;
    }

    const existingUpdate = updatesByOrchestrationId[latestCandidate.orchestration.orchestrationId];
    if (existingUpdate) {
      existingUpdate.slideIds.push(slide.slideId);
      return;
    }

    updatesByOrchestrationId[latestCandidate.orchestration.orchestrationId] = {
      slideIds: [slide.slideId],
      model: latestCandidate.model,
      modelType: latestCandidate.model.modelType,
      orchestration: latestCandidate.orchestration,
    };
  });

  return values(updatesByOrchestrationId);
};

export const buildLatestSpotDetectionSelectionUpdates = ({
  slides,
  slideDetectionsTableArtifacts,
  selectedSlideDetectionsTableOrchestrations,
}: {
  slides: SlideInferenceResults[];
  slideDetectionsTableArtifacts?: ResultNameOrchestrations[];
  selectedSlideDetectionsTableOrchestrations?: OrchestrationBySlideByResultName;
}): LatestSpotDetectionSelectionUpdate[] => {
  const candidates = flatMap(slideDetectionsTableArtifacts, (artifact) => {
    const resultKey = getOrchestrationKey(artifact);
    if (!resultKey) {
      return [];
    }

    return map(artifact.orchestrations, (orchestration) => ({
      resultKey,
      orchestration,
    }));
  });
  const updatesByOrchestrationId: Record<string, LatestSpotDetectionSelectionUpdate> = {};

  forEach(slides, (slide) => {
    if (!isEmpty(selectedSlideDetectionsTableOrchestrations?.[slide.slideId])) {
      return;
    }

    const latestCandidate = maxBy(
      filter(candidates, ({ orchestration }) => includes(orchestration.slideIds, slide.slideId)),
      ({ orchestration }) => getOrchestrationCreatedAtValue(orchestration)
    );

    if (!latestCandidate) {
      return;
    }

    const existingUpdate = updatesByOrchestrationId[latestCandidate.orchestration.orchestrationId];
    if (existingUpdate) {
      existingUpdate.slideIds.push(slide.slideId);
      return;
    }

    updatesByOrchestrationId[latestCandidate.orchestration.orchestrationId] = {
      slideIds: [slide.slideId],
      resultKey: latestCandidate.resultKey,
      orchestration: latestCandidate.orchestration,
    };
  });

  return values(updatesByOrchestrationId);
};
